import { styled } from '@styledComponents';
import { themeColor, themeMediaQuery, themeSpace } from '@utils/styledTheme.util';
import { CTAGatsbyLink } from '@components/CTAGatsbyLink';
import { animationTransition } from '@utils/animationTransition';
import { CTALink } from '@components/CTALink';
import { Select } from '@components/Select/Select';
import { Column, Columns } from '@components/Columns';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';

export const PageContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: ${themeSpace(12)};
  padding-bottom: ${themeSpace(11)};
  ${themeMediaQuery('sm')} {
    padding-top: 11rem;
  }
  ${themeMediaQuery('xl')} {
    padding-top: ${themeSpace(15)};
  }
  > * {
    margin-bottom: ${themeSpace(9)};
    ${themeMediaQuery('sm')} {
      margin-bottom: ${themeSpace(10)};
    }
  }
  z-index: 1;
  position: relative;
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 1380px;
  background: linear-gradient(to bottom, ${themeColor('neutra1')} 47.95%, rgba(249, 245, 240, 0) 70.28%);
  z-index: -1;
`;

interface ProductTypeMenuLinkProps {
  $isActive: boolean;
}

export const ProductTypeMenuLink = styled(CTAGatsbyLink).attrs({
  typography: 'featureCopy17',
  activeClassName: 'active',
  variant: 'neutraLink',
})<ProductTypeMenuLinkProps>`
  flex-direction: column;
  & + & {
    margin-left: ${themeSpace(10)};
  }
  ${({ $isActive, theme }) =>
    $isActive
      ? {
          color: themeColor('neutra6')({ theme }),
        }
      : {}}
  &.active {
    color: ${themeColor('neutra6')};
  }
  &:after {
    content: ' ';
    width: 48px;
    height: 4px;
    border-radius: 4px;
    margin-top: ${themeSpace(3)};
    ${({ $isActive, theme }) =>
      $isActive
        ? {
            background: themeColor('neutra6')({ theme }),
          }
        : {}}
  &.active:after {
    background: ${themeColor('neutra6')};
  }
`;

export const ECardsMenusSpacingStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  > *:not(:last-child) {
    margin-bottom: ${themeSpace(6)};
    ${themeMediaQuery('sm')} {
      margin-bottom: ${themeSpace(7)};
    }
  }
`;

const switchButtonWidth = 172;
const switchButtonWidthMobile = 168;
const switchButtonHeight = 48;
const switchPadding = 6;

export const PrintOnlineMenu = styled.div<{ isRightSelected: boolean; isMounted: boolean }>`
  position: relative;
  width: ${switchButtonWidth * 2 + switchPadding * 2}px;
  background: rgba(220, 212, 201, 0.3);
  border-radius: 100px;
  padding: ${switchPadding}px;
  ${themeMediaQuery('below_sm')} {
    width: ${switchButtonWidthMobile * 2 + switchPadding * 2}px;
  }
  &:after {
    position: absolute;
    content: ' ';
    width: ${switchButtonWidth}px;
    height: ${switchButtonHeight}px;
    border-radius: ${switchButtonHeight / 2}px;
    background: ${themeColor('mono7')};
    box-shadow: 0px 1px 3px -1px rgba(51, 51, 51, 0.1);
    left: ${switchPadding}px;
    transform: translateX(${({ isRightSelected }) => (isRightSelected ? switchButtonWidth : 0)}px);
    visibility: ${({ isMounted }) => (isMounted ? 'visible' : 'hidden')};
    transition: ${animationTransition('transform')};
    top: ${switchPadding}px;
    z-index: 0;
    ${themeMediaQuery('below_sm')} {
      width: ${switchButtonWidthMobile}px;
      transform: translateX(${({ isRightSelected }) => (isRightSelected ? switchButtonWidthMobile : 0)}px);
    }
  }
`;

export const PrintOnlineMenuItem = styled(CTALink).attrs({ typography: 'featureCopy17', variant: 'neutraLink' })<{
  $isActive?: boolean;
}>`
  position: relative;
  width: ${switchButtonWidth}px;
  justify-content: center;
  height: ${switchButtonHeight}px;
  line-height: ${switchButtonHeight}px;
  z-index: 1;
  ${({ $isActive, theme }) => ($isActive ? { color: themeColor('neutra6')({ theme }) } : {})}
  ${themeMediaQuery('below_sm')} {
    width: ${switchButtonWidthMobile}px;
  }
`;

export const CategoriesMenu = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: ${themeSpace(-4)} ${themeSpace(-3)};
  justify-content: center;
`;

type CategoriesMenuItemProps = {
  isSelected?: boolean;
};

export const CategoriesMenuItem = styled(CTALink).attrs<CategoriesMenuItemProps>(({ isSelected }) => ({
  className: isSelected ? 'selected' : '',
}))<CategoriesMenuItemProps>`
  color: ${themeColor('neutra5')};
  padding: 5px ${themeSpace(5)} 6px;
  border: 1px solid ${themeColor('neutra2')};
  border-radius: 20px;
  &:hover {
    color: ${themeColor('neutra6')};
    border-color: ${themeColor('neutra3')};
    background: ${themeColor('mono6')};
  }
  &:active {
    color: ${themeColor('neutra6')};
    border-color: ${themeColor('neutra4')};
    background: ${themeColor('neutra1')};
  }
  &.selected {
    color: ${themeColor('neutra6')};
    border-color: ${themeColor('neutra6')};
    background: ${themeColor('mono7')};
  }
  transition: ${animationTransition('color', 'background-color', 'border-color')};
  margin: ${themeSpace(4)} ${themeSpace(3)};
`;

export const CategoriesMenuColumnsWrapper = styled.div`
  width: 100%;
  ${themeMediaQuery('below_sm')} {
    display: none;
  }
`;

export const CategoriesSelectColumnsWrapper = styled.div`
  width: 100%;
  ${themeMediaQuery('sm')} {
    display: none;
  }
`;

export const CategoriesSelect = styled(Select)`
  width: 100%;
  max-width: 350px;
  margin: auto;
`;

const bannerHeightDesktop = 376;

export const BannerOuterBox = styled(Box)`
  width: 100%;
  height: ${bannerHeightDesktop}px;
  align-self: stretch;
  ${themeMediaQuery('below_sm')} {
    height: 360px;
  }
`;

export const BannerInnerBox = styled(Box)`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const BannerColumns = styled(Columns)`
  height: 100%;
`;

export const BannerColumn = styled(Column)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const CardTypeLink = styled(CTAGatsbyLink).attrs({ variant: 'humanaDarkLink' })`
  justify-content: center;
  ${themeMediaQuery('md')} {
    justify-content: flex-start;
  }
`;

export const BannerImageColumn = styled(Column)`
  height: 100%;
  display: none;
  ${themeMediaQuery('sm')} {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

export const HeroImageWrapper = styled.div`
  text-align: center;
`;

export const HeroImage = styled.img`
  max-width: 100%;
  max-height: ${bannerHeightDesktop}px;
`;

export const StyledReactPaginate = styled(Flex)<{ isDisabled?: boolean }>`
  display: ${({ isDisabled }) => (isDisabled ? 'none' : 'flex')};
  .pagination {
    display: flex;
    width: 100%;
    align-items: flex-end;
    li: not(.next, .previous, .break) {
      width: 45px;
      height: 40px;
      margin: 0 10px 0 10px;
      &.active {
        border-radius: 100%;
        background-color: rgba(128, 128, 128, 0.26);
      }
      a {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
    .next,
    .previous {
      display: none;
    }
  }
`;
